// Packages
import React, { useContext } from 'react';
import { useMediaQuery } from 'react-responsive';

// Hooks
import { useVisitId } from '../../../hooks/useVisitId';
import useContextToken from '../../../hooks/useContextToken';

// Components
import { ButtonLink } from '../../common';

// Context
import { AnalyticsContext } from '../../../analytics/AnalyticsContext';

// Utils
import { getOptimizedImage } from '../../../utils/getOptimizedImage';

// Styles
import './OfferCard.scss';
import {
  IJSONParagraph,
  renderJSONParagraphs,
} from '../../../utils/helpers/jsonParagraphs';

export interface IOfferCardProps {
  slug?: string;
  title: string;
  image: { url: string };
  ctaUrl?: string;
  description: IJSONParagraph[];
  imagePosition?: 'left' | 'right'; // Optional prop to determine image position vs text content
  division?: 'equal' | 'imagePriority' | 'contentPriority';
}

/** Renders an offer card for display on index page */

const OfferCard: React.FC<IOfferCardProps> = ({
  slug,
  title,
  image,
  ctaUrl,
  description,
  imagePosition,
  division,
}) => {
  const { analytics } = useContext(AnalyticsContext);
  const { contextToken } = useContextToken();
  const visitId = useVisitId();

  const isMobile = useMediaQuery({ query: '(max-width: 840px)' });

  const handleOfferTracking = () => {
    analytics.track('selectAction', {
      currPageElemStdName: `OfferCardCTA__view-${slug}-offer`,
      msgCategory: 'navigation',
      msgTriggeredBy: 'user',
      opType: 'buttonClick',
    });
  };

  const useCtaUrl = !!ctaUrl;
  const useSlugAsCtaUrl = !!slug && !ctaUrl;
  const displayCta = !!useCtaUrl || !!useSlugAsCtaUrl;

  const buildCtaUrl = () => {
    if (!ctaUrl) {
      if (!slug) return '';
      else return `/${slug}/`;
    }
    let joinChar = '?';
    if (ctaUrl.includes(joinChar)) joinChar = '&';
    return ctaUrl + joinChar + `visitId=${visitId}${contextToken}`;
  };

  const renderImage = () => (
    <div className="offer-card__image-container">
      <img
        className="offer-card__image aspect-ratio-scalable"
        src={getOptimizedImage({
          url: image.url,
          width: 960,
        })}
      />
    </div>
  );

  const renderCta = () =>
    displayCta ? (
      <ButtonLink
        className="offer-card__cta"
        onClick={handleOfferTracking}
        path={buildCtaUrl()}
        type={'primary'}
        gatsbyLink={useSlugAsCtaUrl}
      >
        Start Now
      </ButtonLink>
    ) : null;

  const renderMobileCard = () => {
    return (
      <>
        <h2 className="offer-card__title">{title}</h2>
        {renderImage()}
        <div className="offer-card__content-container">
          <div className="offer-card__description">
            {renderJSONParagraphs(description)}
          </div>
          {renderCta()}
        </div>
      </>
    );
  };

  const renderDesktopCard = () => {
    return (
      <>
        {imagePosition === 'left' && (
          <div className="offer-card__image-width-constraint image-position-left">
            {renderImage()}
          </div>
        )}
        <div className="offer-card__content-container">
          <h2 className="offer-card__title">{title}</h2>
          <div className="offer-card__description">
            {renderJSONParagraphs(description)}
          </div>
          {renderCta()}
        </div>
        {imagePosition === 'right' && (
          <div className="offer-card__image-width-constraint image-position-right">
            {renderImage()}
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={`offer-card ${isMobile ? `offer-card__mobile` : `offer-card__desktop`} offer-card__image-${imagePosition} offer-card__division-${division}`}
    >
      {isMobile ? renderMobileCard() : renderDesktopCard()}
    </div>
  );
};

export default OfferCard;
